import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Trash, Plus, DownloadSimple, UploadSimple, CalendarBlank, Timer } from "@phosphor-icons/react";

export default function HolidayCalendar() {
  const [lists, setLists] = useState([]);
  const [managers, setManagers] = useState([]);
  const [sel, setSel] = useState(null);        // selected list id
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDefault, setNewDefault] = useState(false);
  const [newMgrs, setNewMgrs] = useState([]);
  const [hDate, setHDate] = useState("");
  const [hName, setHName] = useState("");
  const [importing, setImporting] = useState(false);
  const impRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/timesheet/holiday-lists");
    setLists(data);
    if (data.length && !sel) setSel(data[0].id);
    if (sel && !data.find((l) => l.id === sel)) setSel(data[0]?.id || null);
  };
  useEffect(() => {
    load();
    api.get("/timesheet/managers").then((r) => setManagers(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const active = lists.find((l) => l.id === sel);

  const createList = async () => {
    if (!newName.trim()) return;
    const { data } = await api.post("/timesheet/holiday-lists", { name: newName, is_default: newDefault, manager_ids: newMgrs });
    setNewName(""); setNewDefault(false); setNewMgrs([]); setShowNew(false);
    await load(); setSel(data.id);
    toast.success("Holiday list created");
  };
  const delList = async (id) => {
    if (!window.confirm("Delete this holiday list?")) return;
    await api.delete(`/timesheet/holiday-lists/${id}`);
    setSel(null); await load(); toast.success("Holiday list deleted");
  };
  const toggleMgr = async (list, mid) => {
    const ids = (list.manager_ids || []).includes(mid)
      ? list.manager_ids.filter((x) => x !== mid) : [...(list.manager_ids || []), mid];
    await api.put(`/timesheet/holiday-lists/${list.id}`, { name: list.name, is_default: !!list.is_default, manager_ids: ids, applies_to_sla: !!list.applies_to_sla });
    load();
  };
  const setDefault = async (list, val) => {
    await api.put(`/timesheet/holiday-lists/${list.id}`, { name: list.name, is_default: val, manager_ids: list.manager_ids || [], applies_to_sla: !!list.applies_to_sla });
    load();
  };
  const setSla = async (list, val) => {
    await api.put(`/timesheet/holiday-lists/${list.id}`, { name: list.name, is_default: !!list.is_default, manager_ids: list.manager_ids || [], applies_to_sla: val });
    load(); toast.success(val ? "ITSM SLA clock will now skip these holidays — open tickets re-baselined" : "List no longer applied to ITSM SLAs");
  };
  const addHoliday = async () => {
    if (!hDate || !hName.trim()) { toast.error("Pick a date and enter a name"); return; }
    await api.post(`/timesheet/holiday-lists/${sel}/holidays`, { date: hDate, name: hName });
    setHDate(""); setHName(""); load(); toast.success("Holiday added");
  };
  const delHoliday = async (d) => { await api.delete(`/timesheet/holiday-lists/${sel}/holidays/${d}`); load(); };

  const downloadHolidayTemplate = async () => {
    try {
      const res = await api.get("/timesheet/holiday-lists/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "holiday-list-template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };
  const doImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !sel) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/timesheet/holiday-lists/${sel}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.added} added · ${data.updated} updated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (impRef.current) impRef.current.value = "";
    }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="ts-holidays-tab">
      <div className="flex items-start gap-3 rounded-sm border border-border bg-card/40 px-4 py-3">
        <CalendarBlank size={18} className="text-primary mt-0.5 shrink-0" />
        <div className="text-xs text-muted-foreground leading-relaxed">
          <span className="font-semibold text-foreground">Holiday Calendar</span> is shared by Timesheets and ITSM. Lists mapped to managers drive timesheet expectations;
          lists switched on for <span className="font-semibold text-foreground">ITSM SLA</span> pause every SLA clock on those dates — exactly like Saturdays and Sundays.
          {(() => { const n = lists.filter(l => l.applies_to_sla).length; const d = lists.find(l => l.is_default); return n ? ` ${n} list${n > 1 ? "s" : ""} currently applied to SLAs.` : d ? ` No list is flagged, so the default list "${d.name}" applies to SLAs.` : " No list applies to SLAs yet."; })()}
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
      <Card className="p-4 md:col-span-1" data-testid="ts-holiday-lists-col">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display">Holiday Lists</div>
          <Button size="icon" variant="outline" onClick={() => setShowNew((v) => !v)} data-testid="ts-new-holiday-list-btn"><Plus size={16} /></Button>
        </div>
        {showNew && (
          <div className="space-y-2 mb-3 p-3 rounded-sm border border-primary/20 bg-accent/20" data-testid="ts-new-holiday-list-form">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. India Holidays 2026" data-testid="ts-new-holiday-list-name" />
            <label className="flex items-center gap-2 text-xs"><Switch checked={newDefault} onCheckedChange={setNewDefault} data-testid="ts-new-holiday-default" /> Default (applies when a team has no list)</label>
            <Button size="sm" className="w-full" onClick={createList} data-testid="ts-create-holiday-list-btn">Create list</Button>
          </div>
        )}
        <div className="space-y-1">
          {lists.map((l) => (
            <div key={l.id} className={`flex items-center justify-between px-2 py-1.5 rounded-sm cursor-pointer text-sm ${sel === l.id ? "bg-accent/50" : "hover:bg-accent/30"}`}
              onClick={() => setSel(l.id)} data-testid={`ts-holiday-list-${l.id}`}>
              <span className="truncate">{l.name}{l.is_default && <span className="ml-1.5 text-[9px] font-mono uppercase text-primary">default</span>}{l.applies_to_sla && <span className="ml-1.5 text-[9px] font-mono uppercase text-rag-amber-fg" data-testid={`ts-holiday-sla-badge-${l.id}`}>SLA</span>}<span className="ml-1.5 text-[10px] text-muted-foreground">· {(l.holidays || []).length}d</span></span>
              <Trash size={14} className="text-muted-foreground hover:text-rag-red-fg shrink-0" onClick={(e) => { e.stopPropagation(); delList(l.id); }} />
            </div>
          ))}
          {!lists.length && <div className="text-xs text-muted-foreground py-4 text-center">No holiday lists yet.</div>}
        </div>
      </Card>

      <Card className="p-4 md:col-span-2" data-testid="ts-holiday-detail-col">
        {!active ? <div className="text-sm text-muted-foreground py-8 text-center">Select or create a holiday list.</div> : (
          <div className="space-y-4">
            <div>
              <div className="font-display">{active.name}</div>
              <label className="flex items-center gap-2 text-xs mt-2"><Switch checked={!!active.is_default} onCheckedChange={(v) => setDefault(active, v)} data-testid="ts-holiday-default-toggle" /> Default list (applies to teams with no mapped list)</label>
              <label className="flex items-center gap-2 text-xs mt-2" data-testid="ts-holiday-sla-row"><Switch checked={!!active.applies_to_sla} onCheckedChange={(v) => setSla(active, v)} data-testid="ts-holiday-sla-toggle" /> <Timer size={13} className="text-primary" /> Applies to ITSM SLAs — SLA timers pause on these dates (like weekends)</label>
            </div>

            <div>
              <div className="text-xs font-mono uppercase text-muted-foreground mb-1.5">Applies to teams (managers)</div>
              <div className="flex flex-wrap gap-1.5" data-testid="ts-holiday-managers">
                {managers.map((m) => {
                  const on = (active.manager_ids || []).includes(m.id);
                  return (
                    <button key={m.id} onClick={() => toggleMgr(active, m.id)} data-testid={`ts-holiday-mgr-${m.id}`}
                      className={`px-2.5 py-1 rounded-sm text-xs border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-accent/40"}`}>{m.name}</button>
                  );
                })}
                {!managers.length && <span className="text-xs text-muted-foreground">No managers found.</span>}
              </div>
            </div>

            <div className="border-t border-border pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-mono uppercase text-muted-foreground">Holidays</div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-sm text-[11px] h-8" onClick={downloadHolidayTemplate} data-testid="ts-holiday-template-btn"><DownloadSimple size={13} className="mr-1.5" />Template</Button>
                  <input ref={impRef} type="file" accept=".xlsx,.xlsm,.csv" onChange={doImport} className="hidden" data-testid="ts-holiday-import-input" />
                  <Button size="sm" className="rounded-sm text-[11px] h-8" disabled={importing} onClick={() => impRef.current?.click()} data-testid="ts-holiday-import-btn"><UploadSimple size={13} className="mr-1.5" />{importing ? "Importing…" : "Import Excel/CSV"}</Button>
                </div>
              </div>
              <div className="flex gap-2 mb-3">
                <Input type="date" value={hDate} onChange={(e) => setHDate(e.target.value)} className="w-44" data-testid="ts-holiday-date-input" />
                <Input value={hName} onChange={(e) => setHName(e.target.value)} placeholder="e.g. Republic Day" data-testid="ts-holiday-name-input" />
                <Button size="icon" onClick={addHoliday} data-testid="ts-add-holiday-btn"><Plus size={16} /></Button>
              </div>
              <div className="space-y-1">
                {(active.holidays || []).map((h) => (
                  <div key={h.date} className="flex items-center justify-between px-2 py-1.5 rounded-sm text-sm hover:bg-accent/30" data-testid={`ts-holiday-${h.date}`}>
                    <span><span className="font-mono text-muted-foreground mr-2">{h.date}</span>{h.name}</span>
                    <Trash size={14} className="text-muted-foreground hover:text-rag-red-fg cursor-pointer" onClick={() => delHoliday(h.date)} />
                  </div>
                ))}
                {!(active.holidays || []).length && <div className="text-xs text-muted-foreground py-3 text-center">No holidays added yet.</div>}
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
    </div>
  );
}
